import { useEffect, useMemo, useState } from 'react';

import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';

import * as FiscalXmlObservacaoAPI from '../../../api/fiscalXmlObservacao';
import { buscarProdutosSemelhantes } from '../../../api/produto';

function exibirValor(value) {
    if (value === null || value === undefined || value === '') {
        return '—';
    }

    if (typeof value === 'boolean') {
        return value ? 'Sim' : 'Não';
    }

    return String(value);
}

function formatarDataHora(value) {
    if (!value) return '—';

    const data = new Date(value);

    return Number.isNaN(data.getTime()) ? '—' : data.toLocaleString('pt-BR');
}

function corStatus(status) {
    const cores = {
        PENDENTE_REVISAO: 'warning',
        PENDENTE_VINCULO: 'error',
        APLICADA: 'success',
        IGNORADA: 'default',
    };

    return cores[status] || 'default';
}

function corComparacao(status) {
    const cores = {
        COINCIDENTE: 'success',
        AUSENTE_CADASTRO: 'warning',
        DIVERGENTE: 'error',
        NAO_OBSERVADO: 'default',
    };

    return cores[status] || 'default';
}

function ObservacaoFiscalDialog({ open, uuid, onClose, onAtualizado }) {
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');

    const [confirmandoIgnorar, setConfirmandoIgnorar] = useState(false);
    const [motivo, setMotivo] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erroDecisao, setErroDecisao] = useState('');

    const [pesquisaProduto, setPesquisaProduto] = useState('');
    const [produtosSemelhantes, setProdutosSemelhantes] = useState([]);
    const [produtoSelecionado, setProdutoSelecionado] = useState(null);
    const [buscandoProdutos, setBuscandoProdutos] = useState(false);
    const [erroBuscaProduto, setErroBuscaProduto] = useState('');

    const [confirmandoAplicar, setConfirmandoAplicar] = useState(false);

    useEffect(() => {
        let ativo = true;

        async function carregar() {
            if (!open || !uuid) return;

            setCarregando(true);
            setErro('');
            setDados(null);

            setPesquisaProduto('');
            setProdutosSemelhantes([]);
            setProdutoSelecionado(null);
            setErroBuscaProduto('');

            setConfirmandoAplicar(false);

            try {
                const response = await FiscalXmlObservacaoAPI.consultar(uuid);

                if (ativo) {
                    setDados(response.data.data);

                    setPesquisaProduto(response.data.data?.payloadObservado?.produtoFornecedor?.descricao || '');
                }
            } catch (error) {
                if (ativo) {
                    setErro(error.response?.data?.message || 'Não foi possível carregar a observação fiscal.');
                }
            } finally {
                if (ativo) {
                    setCarregando(false);
                }
            }
        }

        carregar();

        return () => {
            ativo = false;
        };
    }, [open, uuid]);

    const comparacoes = useMemo(
        () => (Array.isArray(dados?.analise?.comparacao?.comparacoes) ? dados.analise.comparacao.comparacoes : []),
        [dados]
    );

    const regraObservada = dados?.payloadObservado?.regra || {};

    const classificacao = dados?.payloadObservado?.classificacao || {};

    const contextoXml = dados?.analise?.fonte?.contextoXml || {};

    const camposFiscais = [
        ['CST ICMS', regraObservada.cstIcms],
        ['CSOSN', regraObservada.csosn],
        ['Alíquota ICMS', regraObservada.aliquotaIcms],
        ['Redução base ICMS', regraObservada.reducaoBaseIcms],
        ['MVA ICMS-ST', regraObservada.mvaIcmsSt],
        ['Alíquota ICMS-ST', regraObservada.aliquotaIcmsSt],
        ['Redução base ICMS-ST', regraObservada.reducaoBaseIcmsSt],
        ['Alíquota FCP', regraObservada.aliquotaFcp],
        ['Alíquota FCP-ST', regraObservada.aliquotaFcpSt],
        ['FCP-ST retido', regraObservada.aliquotaFcpStRetido],
        ['CST IPI', regraObservada.cstIpi],
        ['Alíquota IPI', regraObservada.aliquotaIpi],
        ['CST PIS', regraObservada.cstPis],
        ['Alíquota PIS', regraObservada.aliquotaPis],
        ['Pauta PIS', regraObservada.valorPautaPis],
        ['CST COFINS', regraObservada.cstCofins],
        ['Alíquota COFINS', regraObservada.aliquotaCofins],
        ['Pauta COFINS', regraObservada.valorPautaCofins],
        ['Unidade da pauta', regraObservada.unidadePauta],
        ['CST IBS/CBS', regraObservada.cstIbsCbs],
        ['Classificação IBS/CBS', regraObservada.cClassTrib],
    ];

    const podeIgnorar =
        dados &&
        (['PENDENTE_REVISAO', 'PENDENTE_VINCULO'].includes(dados.status) ||
            (dados.status === 'IGNORADA' && dados.decisao?.automatica === true));

    function fechar() {
        if (salvando) return;

        onClose();
    }

    async function confirmarIgnorar() {
        if (salvando || !podeIgnorar) return;

        const motivoNormalizado = motivo.replace(/\s+/g, ' ').trim();

        if (!motivoNormalizado) {
            setErroDecisao('Informe a justificativa para ignorar esta observação.');
            return;
        }

        if (motivoNormalizado.length > 2000) {
            setErroDecisao('A justificativa deve possuir no máximo 2000 caracteres.');
            return;
        }

        setSalvando(true);
        setErroDecisao('');

        try {
            const response = await FiscalXmlObservacaoAPI.ignorar(uuid, motivoNormalizado);
            const resultado = response.data.data;

            setDados((atual) => ({
                ...atual,
                ...resultado,
                decisao: {
                    ...atual?.decisao,
                    tipo: 'IGNORADA_MANUALMENTE',
                    automatica: false,
                    motivo: resultado.motivoDecisao,
                },
            }));

            setConfirmandoIgnorar(false);
            setMotivo('');
        } catch (error) {
            setErroDecisao(error.response?.data?.message || 'Não foi possível ignorar a observação fiscal.');
            return;
        } finally {
            setSalvando(false);
        }

        onAtualizado?.();
        onClose();
    }

    const empresaObservacaoId = dados?.empresaId;
    const statusObservacao = dados?.status;

    useEffect(() => {
        const descricao = pesquisaProduto.trim();

        if (!open || statusObservacao !== 'PENDENTE_VINCULO' || !empresaObservacaoId || descricao.length < 3) {
            setProdutosSemelhantes([]);
            setBuscandoProdutos(false);
            return;
        }

        let ativo = true;

        setBuscandoProdutos(true);
        setErroBuscaProduto('');
        setProdutosSemelhantes([]);

        const timer = setTimeout(async () => {
            try {
                const response = await buscarProdutosSemelhantes({
                    empresaId: empresaObservacaoId,
                    descricao,
                    limite: 10,
                });

                if (ativo) {
                    setProdutosSemelhantes(Array.isArray(response?.produtos) ? response.produtos : []);
                }
            } catch (error) {
                if (ativo) {
                    setErroBuscaProduto(error.response?.data?.message || 'Não foi possível pesquisar os produtos.');
                }
            } finally {
                if (ativo) {
                    setBuscandoProdutos(false);
                }
            }
        }, 500);

        return () => {
            ativo = false;
            clearTimeout(timer);
        };
    }, [open, uuid, empresaObservacaoId, statusObservacao, pesquisaProduto]);

    async function confirmarVinculo() {
        if (salvando || dados?.status !== 'PENDENTE_VINCULO' || !produtoSelecionado?.id) {
            return;
        }

        setSalvando(true);
        setErroDecisao('');

        try {
            await FiscalXmlObservacaoAPI.vincular(uuid, Number(produtoSelecionado.id));
        } catch (error) {
            setErroDecisao(error.response?.data?.message || 'Não foi possível vincular o produto.');
            return;
        } finally {
            setSalvando(false);
        }

        onAtualizado?.();
        onClose();
    }

    const podeAplicar =
        dados?.status === 'PENDENTE_REVISAO' && dados?.classificacaoFonte === 'CANDIDATA_REVISAO' && Boolean(dados?.produtoId);

    async function confirmarAplicacao() {
        if (salvando || !podeAplicar) return;

        setSalvando(true);
        setErroDecisao('');

        try {
            await FiscalXmlObservacaoAPI.aplicar(
                uuid,
                'Aplicação ao cadastro confirmada pelo usuário, com regra pendente de revisão.'
            );
        } catch (error) {
            setErroDecisao(error.response?.data?.message || 'Não foi possível aplicar a observação fiscal.');
            return;
        } finally {
            setSalvando(false);
        }

        onAtualizado?.();
        onClose();
    }
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>Revisão da observação fiscal</DialogTitle>

            <DialogContent dividers>
                {carregando && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 6,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                {erro && <Alert severity="error">{erro}</Alert>}

                {!carregando && !erro && dados && (
                    <Stack spacing={2}>
                        {dados.status === 'IGNORADA' && dados.motivoDecisao && (
                            <Alert severity="info">
                                Observação ignorada: {dados.motivoDecisao}
                                <br />
                                Revisado em: {formatarDataHora(dados.revisadoEm)}
                            </Alert>
                        )}

                        {dados.status === 'PENDENTE_VINCULO' && !confirmandoIgnorar && (
                            <Box>
                                <Typography variant="subtitle1" gutterBottom>
                                    Vincular produto do cadastro
                                </Typography>

                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    Produto no XML: {exibirValor(dados.payloadObservado?.produtoFornecedor?.descricao)}
                                    {' — Código no fornecedor: '}
                                    {exibirValor(dados.codigoProdutoFornecedor)}
                                </Typography>

                                <TextField
                                    label="Pesquisar produto"
                                    value={pesquisaProduto}
                                    onChange={(event) => {
                                        setPesquisaProduto(event.target.value);
                                        setProdutoSelecionado(null);
                                        setProdutosSemelhantes([]);
                                        setErroBuscaProduto('');
                                        setErroDecisao('');
                                    }}
                                    fullWidth
                                    disabled={salvando}
                                    helperText="Digite pelo menos 3 caracteres da descrição."
                                    sx={{ mb: 2 }}
                                />

                                {erroBuscaProduto && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {erroBuscaProduto}
                                    </Alert>
                                )}

                                {buscandoProdutos ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CircularProgress size={20} />
                                        <Typography variant="body2">Procurando produtos semelhantes...</Typography>
                                    </Stack>
                                ) : (
                                    <>
                                        {!erroBuscaProduto &&
                                            pesquisaProduto.trim().length >= 3 &&
                                            produtosSemelhantes.length === 0 && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Nenhum produto semelhante localizado.
                                                </Typography>
                                            )}

                                        {produtosSemelhantes.map((produto) => {
                                            const selecionado = Number(produtoSelecionado?.id) === Number(produto.id);

                                            return (
                                                <Box
                                                    component="button"
                                                    type="button"
                                                    key={produto.uuid ?? produto.id}
                                                    disabled={salvando}
                                                    aria-pressed={selecionado}
                                                    onClick={() => {
                                                        setProdutoSelecionado(produto);
                                                        setErroDecisao('');
                                                    }}
                                                    sx={{
                                                        display: 'block',
                                                        width: '100%',
                                                        textAlign: 'left',
                                                        font: 'inherit',
                                                        color: 'text.primary',
                                                        p: 1.5,
                                                        mb: 1,
                                                        border: '1px solid',
                                                        borderColor: selecionado ? 'primary.main' : 'divider',
                                                        borderRadius: 1,
                                                        cursor: salvando ? 'default' : 'pointer',
                                                        bgcolor: selecionado ? 'action.selected' : 'background.paper',
                                                    }}
                                                >
                                                    <Typography component="span" display="block" fontWeight={600}>
                                                        {produto.descricao}
                                                    </Typography>

                                                    <Typography component="span" display="block" variant="body2">
                                                        Código: {produto.codigo ?? '—'}
                                                        {' — Grupo: '}
                                                        {produto.Grupo?.descricao ?? '—'}
                                                    </Typography>

                                                    <Typography component="span" display="block" variant="body2">
                                                        NCM: {produto.NCM?.ncmFormatado ?? produto.NCM?.ncm ?? '—'}
                                                    </Typography>

                                                    {produto.produtoEmbalagens?.map((embalagem) => (
                                                        <Typography
                                                            component="span"
                                                            key={embalagem.id}
                                                            variant="caption"
                                                            display="block"
                                                        >
                                                            {embalagem.Embalagem?.descricao}
                                                            {' — '}
                                                            {embalagem.codigoBarras}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            );
                                        })}
                                    </>
                                )}

                                {produtoSelecionado && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        Produto selecionado: {produtoSelecionado.descricao}. Ao confirmar, a observação será
                                        encaminhada para revisão.
                                    </Alert>
                                )}
                            </Box>
                        )}
                        <Stack
                            direction={{
                                xs: 'column',
                                md: 'row',
                            }}
                            spacing={1}
                            alignItems={{
                                xs: 'flex-start',
                                md: 'center',
                            }}
                        >
                            <Chip label={dados.status} color={corStatus(dados.status)} />

                            <Chip label={dados.classificacaoFonte} variant="outlined" />

                            <Typography variant="body2">Atualizado em: {formatarDataHora(dados.updatedAt)}</Typography>
                        </Stack>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Documento
                            </Typography>

                            <Typography variant="body2">Empresa: {exibirValor(dados.Empresa?.nomeFantasia)}</Typography>

                            <Typography variant="body2">Chave de acesso: {exibirValor(dados.chaveAcesso)}</Typography>

                            <Typography variant="body2">Item: {exibirValor(dados.numeroItem)}</Typography>

                            <Typography variant="body2">Fornecedor: {exibirValor(dados.Fornecedor?.descricao)}</Typography>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Produto vinculado
                            </Typography>

                            <Typography variant="body2">Código: {exibirValor(dados.Produto?.codigo)}</Typography>

                            <Typography variant="body2">Descrição: {exibirValor(dados.Produto?.descricao)}</Typography>

                            <Typography variant="body2">
                                Perfil fiscal: {exibirValor(dados.Produto?.FiscalPerfil?.descricao)}
                            </Typography>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Contexto observado no XML
                            </Typography>

                            <Typography variant="body2">CRT do emitente: {exibirValor(contextoXml.emitenteCrt)}</Typography>

                            <Typography variant="body2">Operação: {exibirValor(classificacao.cfop)}</Typography>

                            <Typography variant="body2">
                                UF origem/destino: {exibirValor(contextoXml.ufOrigem)} / {exibirValor(contextoXml.ufDestino)}
                            </Typography>

                            <Typography variant="body2">
                                Tipo do destinatário: {exibirValor(contextoXml.tipoDestinatario)}
                            </Typography>

                            <Typography variant="body2">Consumidor final: {exibirValor(contextoXml.consumidorFinal)}</Typography>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Comparação com o cadastro
                            </Typography>

                            {comparacoes.length === 0 ? (
                                <Alert severity="info">Não existem comparações disponíveis.</Alert>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Campo</TableCell>
                                                <TableCell>XML</TableCell>
                                                <TableCell>Cadastro</TableCell>
                                                <TableCell>Situação</TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {comparacoes.map((item) => (
                                                <TableRow key={item.campo}>
                                                    <TableCell>{item.campo}</TableCell>

                                                    <TableCell>{exibirValor(item.xml)}</TableCell>

                                                    <TableCell>{exibirValor(item.cadastro)}</TableCell>

                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={item.status}
                                                            color={corComparacao(item.status)}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Tributação observada
                            </Typography>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Campo</TableCell>
                                            <TableCell>Valor</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {camposFiscais.map(([campo, valor]) => (
                                            <TableRow key={campo}>
                                                <TableCell>{campo}</TableCell>

                                                <TableCell>{exibirValor(valor)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Stack>
                )}
            </DialogContent>

            {!carregando && !erro && dados && (
                <Box sx={{ px: 3, pt: 2 }}>
                    {erroDecisao && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {erroDecisao}
                        </Alert>
                    )}

                    {confirmandoIgnorar && (
                        <Stack spacing={2} sx={{ mb: 2 }}>
                            <Alert severity="warning">
                                Esta decisão ignora a observação fiscal e registra sua justificativa. O produto e o perfil fiscal
                                permanecem inalterados.
                            </Alert>

                            <TextField
                                label="Justificativa"
                                value={motivo}
                                onChange={(event) => {
                                    setMotivo(event.target.value);
                                    setErroDecisao('');
                                }}
                                required
                                multiline
                                minRows={3}
                                fullWidth
                                autoFocus
                                disabled={salvando}
                                inputProps={{ maxLength: 2000 }}
                                helperText={`${motivo.length}/2000 caracteres`}
                            />
                        </Stack>
                    )}
                </Box>
            )}

            <DialogActions>
                {confirmandoAplicar ? (
                    <Stack spacing={2} sx={{ width: '100%', p: 2 }}>
                        <Alert severity="warning">
                            Esta ação atualiza o NCM, CEST e a origem da mercadoria quando presentes no XML, associa o perfil
                            fiscal ao produto e cria uma regra pendente de revisão. A nova regra ficará com a integração
                            desativada.
                        </Alert>

                        <Typography variant="body2">Produto: {exibirValor(dados?.Produto?.descricao)}</Typography>

                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                                disabled={salvando}
                                onClick={() => {
                                    setConfirmandoAplicar(false);
                                    setErroDecisao('');
                                }}
                            >
                                Cancelar
                            </Button>

                            <Button
                                variant="contained"
                                disabled={salvando || !podeAplicar}
                                onClick={confirmarAplicacao}
                                startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : undefined}
                            >
                                {salvando ? 'Aplicando...' : 'Confirmar aplicação'}
                            </Button>
                        </Stack>
                    </Stack>
                ) : confirmandoIgnorar ? (
                    <>
                        <Button
                            disabled={salvando}
                            onClick={() => {
                                setConfirmandoIgnorar(false);
                                setMotivo('');
                                setErroDecisao('');
                            }}
                        >
                            Cancelar
                        </Button>

                        <Button
                            color="warning"
                            variant="contained"
                            disabled={salvando || !motivo.trim()}
                            onClick={confirmarIgnorar}
                            startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : undefined}
                        >
                            {salvando ? 'Salvando...' : 'Confirmar ignorar'}
                        </Button>
                    </>
                ) : (
                    <>
                        {!carregando && !erro && dados?.status === 'PENDENTE_VINCULO' && (
                            <Button
                                variant="contained"
                                disabled={salvando || buscandoProdutos || !produtoSelecionado?.id}
                                onClick={confirmarVinculo}
                                startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : undefined}
                            >
                                {salvando ? 'Vinculando...' : 'Confirmar vínculo'}
                            </Button>
                        )}

                        {!carregando && !erro && podeAplicar && (
                            <Button
                                variant="contained"
                                disabled={salvando}
                                onClick={() => {
                                    setErroDecisao('');
                                    setConfirmandoAplicar(true);
                                }}
                            >
                                Aplicar ao cadastro
                            </Button>
                        )}

                        {!carregando && !erro && podeIgnorar && (
                            <Button
                                color="warning"
                                disabled={salvando}
                                onClick={() => {
                                    setErroDecisao('');
                                    setConfirmandoIgnorar(true);
                                }}
                            >
                                Ignorar observação
                            </Button>
                        )}

                        <Button onClick={fechar} disabled={salvando}>
                            Fechar
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default ObservacaoFiscalDialog;
